import { Copy } from "lucide-react";
import { useState } from "react";

// Constants
const COPY_SUCCESS_TIMEOUT = 2000;
const BUTTON_HOVER_OPACITY = 0.9;

export default function LlmActions() {
  const [copied, setCopied] = useState(false);

  // Don't render on API documentation pages
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/api")) {
    return null;
  }

  // Helper function to extract content from DOM
  const getContentFromDOM = () => {
    // Find the main content area - try multiple selectors
    const contentSelectors = [
      'main [data-pagefind-body]', // Pagefind indexed content (most specific)
      'main article .prose', // Zudoku prose content
      'main article', // Article wrapper
      'main .typography', // Typography wrapper
      'main [role="main"]', // Main role
      'main', // Fallback to entire main
    ];

    let contentElement: Element | null = null;
    for (const selector of contentSelectors) {
      contentElement = document.querySelector(selector);
      if (contentElement) break;
    }

    if (!contentElement) return "";

    // Clone to avoid modifying the actual DOM
    const clone = contentElement.cloneNode(true) as HTMLElement;

    // Remove unwanted elements - be aggressive about filtering UI chrome
    const selectorsToRemove = [
      'nav', // Navigation
      'aside', // Sidebars
      'button', // All buttons
      '[role="navigation"]', // ARIA navigation
      '.breadcrumb', // Breadcrumbs
      '.table-of-contents', // ToC
      'footer', // Footers
      'time', // Timestamps
      '[class*="last-modified"]', // Last modified
      '[class*="Last modified"]', // Case variation
      '[class*="on-this-page"]', // On this page
      '[class*="On this page"]', // Case variation
      'script', // Scripts
      'style', // Styles
      'a[href^="#"]', // Anchor links (usually nav)
      '[data-pagefind-ignore]', // Explicitly ignored content
    ];

    selectorsToRemove.forEach(selector => {
      clone.querySelectorAll(selector).forEach(el => el.remove());
    });

    // Get text content and clean up
    const text = clone.textContent || "";
    const lines = text
      .split('\n')
      .map(line => line.trim())
      .filter(line => {
        // Filter out empty lines and common UI chrome text
        if (line.length === 0) return false;
        if (line === "Copy this page's content for use with AI assistants") return false;
        if (line.startsWith("Last modified on")) return false;
        if (line === "On this page") return false;
        return true;
      });

    return lines.join('\n\n');
  };

  if (typeof window === "undefined") return null;

  const handleCopy = async () => {
    try {
      const contentToCopy = getContentFromDOM();

      if (!contentToCopy || contentToCopy.trim().length === 0) {
        console.error("No content to copy");
        return;
      }

      await navigator.clipboard.writeText(contentToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), COPY_SUCCESS_TIMEOUT);
    } catch (error) {
      console.error("Failed to copy content:", error);
    }
  };

  return (
    <div
      style={{
        marginBottom: "24px",
        padding: "12px 16px",
        backgroundColor: "var(--zd-colors-surface)",
        border: "1px solid var(--zd-colors-border)",
        borderRadius: "8px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: "12px",
      }}
    >
      <div style={{ fontSize: "13px", color: "var(--zd-colors-text-subtle)" }}>
        Copy this page's content for use with AI assistants
      </div>
      <button
        onClick={handleCopy}
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "6px",
          padding: "6px 14px",
          backgroundColor: copied ? "var(--zd-colors-success)" : "var(--zd-colors-primary)",
          color: copied ? "var(--zd-colors-success-foreground, white)" : "var(--zd-colors-primary-foreground, white)",
          border: "none",
          borderRadius: "6px",
          cursor: "pointer",
          fontSize: "13px",
          fontWeight: 500,
          transition: "all 0.15s ease",
          whiteSpace: "nowrap",
        }}
        onMouseEnter={(e) => {
          if (!copied) {
            e.currentTarget.style.opacity = String(BUTTON_HOVER_OPACITY);
          }
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.opacity = "1";
        }}
        title="Copy page content for LLM"
      >
        <Copy size={14} />
        {copied ? "Copied!" : "Copy for LLM"}
      </button>
    </div>
  );
}
